import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { OptionsObject, useSnackbar } from "notistack";
import { Box, Grid, Link, TextField, Typography } from "@material-ui/core";
import { SnackbarContentProps } from "@material-ui/core/SnackbarContent";
import { Button } from "components/Forms/StyledComponents";
import { OptionGroup, StyledPaper } from "components/Styled";
import PageLoading from "components/PageLoading";
import apiRequest, { isErrorWithCode } from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import useFormState from "utils/useFormState";
import canUseTemplate, { AllowedStorage } from "utils/canUseTemplates";
import Mappings from "./Mappings";
import StorageAccountSelector from "components/StorageAccounts/StorageAccountsSelector";
import {
  StorageAccountUpdate,
  StorageAccountValue,
} from "components/StorageAccounts/types";
import SelectMetadataKey from "./SelectMetadataKey";
import NameField from "./NameField";
import DescriptionField from "./DescriptionField";
import {
  TemplateDefinitionReqBody,
  StorageAccount,
  StorageTemplate,
  TemplateDefinition,
  StorageProvider,
} from "@dashboard-v3/api";
import { FormState } from "../types";
import { StyledTitle } from "../Styled";
import { LoadError } from "./LoadError";
import LoadErrorDialog from "./LoadErrorDialog";
import ReadOnlyMappings from "./ReadOnlyMappings";
import { Alert } from "@material-ui/lab";
import { FormTouched, hasErrors, validate } from "./validation";
import random from "utils/random";
import {
  matchesStorageType,
  toStorageType,
  storageTypes,
} from "utils/storageProviders";

type AccountWithState = StorageAccount & { enabled: boolean };

export default function Form() {
  const { t } = useTranslation(["templateDefinitions", "rules"]);
  const history = useHistory();
  const { state, pathname } = useLocation();
  const { id: paramId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [accounts, setAccounts] = useState<AccountWithState[]>([]);
  const [templates, setTemplates] = useState<StorageTemplate[]>([]);
  const [loadError, setLoadError] = useState<LoadError>(null);
  const [touched, setTouched] = useState<FormTouched>({});
  const [templateNotFound, setTemplateNotFound] = useState<boolean>(false);
  const [unsyncedAccounts, setUnsyncedAccounts] = useState<AccountWithState[]>(
    []
  );
  const [failedAccountIds, setFailedAccountIds] = useState<string[]>([]);
  const [editingStorageType, setEditingStorageType] =
    useState<StorageProvider>(null);

  const { formState, ...form } = useFormState<FormState>(initValues());
  const isEditing: boolean = (pathname as string).endsWith("/edit");

  const showMappingFields = Boolean(formState.metadataKey);
  const template = templates?.find(m => m.id === formState.metadataKey) || null;
  const errors = validate(formState, t, {
    skipMappings: templateNotFound || unsyncedAccounts.length > 0,
  });
  const storageAccountError = Boolean(
    touched.storageAccount && errors.storageAccount
  );
  const storageAccountErrorText =
    (touched.storageAccount && errors.storageAccount) || "";

  useEffect(() => {
    initialLoading();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initialLoading() {
    const accounts = await loadStorageAccounts();
    const isNotAccountsAvailable =
      accounts.length === 0 || accounts.every(account => !account.enabled);

    if (isNotAccountsAvailable) {
      setLoadError("NO_ACCOUNTS_ENABLED");
      return;
    }

    if (isEditing) {
      setLoading(true);
      await populateEditForm(accounts);
      setLoading(false);
    }
  }

  async function populateEditForm(accounts: AccountWithState[]) {
    const { setValues } = form;

    const { storageType, ...definition } = await getDefinitionUsingParam();
    setEditingStorageType(storageType);
    const accountOfSameType = filterByType(accounts, storageType);

    if (accountOfSameType.length === 0) {
      setLoadError("NO_ACCOUNTS_ENABLED");
    }

    const [templates, accountUse, notSyncedAccounts] =
      await fetchTemplatesFromSomeAccount(
        accountOfSameType,
        definition.templateId
      );

    if (!accountUse && notSyncedAccounts.length > 0) {
      setFailedAccountIds(notSyncedAccounts.map(a => a.id));
      setUnsyncedAccounts(notSyncedAccounts);
      setValues({
        name: definition.name,
        description: definition.description,
        metadataKey: definition.templateId,
        mappings: convertMappingToKV(definition.mappings),
        storageAccount: "",
      });
      return;
    }

    setTemplates(templates);
    const templateFound = templates.some(t => t.id === definition.templateId);
    if (!templateFound) setTemplateNotFound(true);

    setValues({
      name: definition.name,
      storageAccount: accountUse?.id,
      description: definition.description,
      metadataKey: definition.templateId,
      mappings: convertMappingToKV(definition.mappings),
    });
  }

  async function loadStorageAccounts() {
    const data = await apiRequest<StorageAccount[]>("GET", "/storage-accounts");

    const accounts = data.reduce((accounts, account) => {
      if (canUseTemplate(account.type)) {
        accounts.push({ ...account, enabled: account.synced });
      }
      return accounts;
    }, []);

    setAccounts(accounts);
    return accounts;
  }

  async function getDefinitionUsingParam(): Promise<TemplateDefinition> {
    if (state) {
      const { selectedDefinition, definitionId } = state;
      return definitionId !== paramId
        ? fetchDefinitionById(paramId)
        : selectedDefinition;
    }
    return fetchDefinitionById(paramId);
  }

  async function fetchDefinitionById(id: string) {
    setLoading(true);
    try {
      const res = await apiRequest<TemplateDefinition>(
        "GET",
        `/template-definitions/${id}`
      );
      setLoading(false);
      return res;
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(t("errors.definitions.getError"), {
        variant: "error",
      });
      history.push("/template-definitions");
    }
  }

  function toStorageAccountValue(): StorageAccountValue {
    const { storageAccount } = formState;
    if (!storageAccount) return undefined;

    return { id: storageAccount };
  }

  function excludedProviders(): StorageProvider[] {
    return storageTypes.filter(type => {
      if (!canUseTemplate(type)) return true;
      if (!editingStorageType) return false;
      return !matchesStorageType(type, editingStorageType);
    });
  }

  async function handleSelectStorageAccount(change: StorageAccountUpdate) {
    const { setValues, reset } = form;
    const storageAccountId = change.id;
    setValues({ storageAccount: storageAccountId || "" });

    if (!storageAccountId) {
      form.reset(["metadataKey", "mappings"]);
      resetDependentTouched();
      return;
    }

    try {
      setLoading(true);
      const templates = await fetchTemplatesFromStorage(storageAccountId);
      setTemplates(templates);
      setUnsyncedAccounts([]);
      setTemplateNotFound(false);

      const sameMetadataKey =
        formState.metadataKey &&
        templates.some(t => t.id === formState.metadataKey);

      if (sameMetadataKey) return;

      reset(["metadataKey", "mappings"]);
      resetDependentTouched();
    } catch (error) {
      await handleTemplateFetchError(error, storageAccountId);
    } finally {
      setLoading(false);
    }
  }

  function clearTouched(fields: Array<keyof FormTouched>) {
    setTouched(prev => {
      const next = { ...prev };
      fields.forEach(field => {
        if (field === "mappings") next.mappings = [];
        else next[field] = false;
      });
      return next;
    });
  }

  function resetDependentTouched() {
    clearTouched(["metadataKey", "mappings"]);
  }

  const handleMetadataKeyChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    form.reset(["mappings"]);
    setTouched(prev => ({ ...prev, mappings: [] }));
    form.updateValues(e);
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    const fieldName = e.target.name;
    if (!fieldName) return;
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleMappingBlur = (index: number) => {
    setTouched(prev => {
      const next = [...(prev.mappings || [])];
      next[index] = { key: true, value: true };
      return { ...prev, mappings: next };
    });
  };

  const addMapping = () => {
    form.setValues({
      mappings: [...formState.mappings, { id: random(), key: "", value: "" }],
    });
    setTouched(prev => ({
      ...prev,
      mappings: [...(prev.mappings || []), {}],
    }));
  };

  const removeMapping = (index: number) => {
    if (formState.mappings.length <= 1) return;
    form.setValues({
      mappings: formState.mappings.filter((_, i) => i !== index),
    });

    setTouched(prev => ({
      ...prev,
      mappings: (prev.mappings || []).filter((_, i) => i !== index),
    }));
  };

  const markAllTouched = () => {
    setTouched({
      name: true,
      description: true,
      storageAccount: true,
      metadataKey: true,
      mappings: formState.mappings.map(() => ({ key: true, value: true })),
    });
  };

  async function handleTemplateFetchError(
    error: unknown,
    storageAccount: string
  ) {
    console.error("Storage Template Fetch Error: ", error);

    const updatedFailedIds = [...failedAccountIds, storageAccount];
    setFailedAccountIds(updatedFailedIds);

    const usableLeft = accounts.some(
      account => account.enabled && !updatedFailedIds.includes(account.id)
    );

    if (!usableLeft) {
      // No enabled accounts left
      setLoadError("NO_ACCOUNTS_ENABLED");
      setTemplates([]);
      form.reset(["storageAccount", "metadataKey", "mappings"]);
      clearTouched(["storageAccount", "metadataKey", "mappings"]);
      return;
    }

    switch (true) {
      case isAccountNotSynced(error):
        enqueueSnackbar(
          t("errors.account.notSynced"),
          errorSnackbarOptions("td-snackbar-not-synced")
        );
        break;
      case isBoxNotEnterprise(error):
        enqueueSnackbar(t("errors.account.boxNotEnterprise"), {
          variant: "error",
        });
        break;
      case isAccountNotFound(error):
        enqueueSnackbar(t("errors.account.notFound"), {
          variant: "error",
        });
        break;
      default:
        enqueueSnackbar(
          t("errors.templates.getError"),
          errorSnackbarOptions("td-snackbar-templates-error")
        );
        break;
    }
    if (isEditing) {
      form.reset(["storageAccount"]);
      clearTouched(["storageAccount"]);
    } else {
      setTemplates([]);
      form.reset(["storageAccount", "metadataKey", "mappings"]);
      clearTouched(["storageAccount", "metadataKey", "mappings"]);
    }
  }

  const saveDefinition = async (
    body: TemplateDefinitionReqBody,
    id?: string
  ) => {
    setLoading(true);
    try {
      const method = id ? "PUT" : "POST";
      const path = id ? `/template-definitions/${id}` : "/template-definitions";
      const res = await apiRequest<TemplateDefinition>(method, path, body);
      enqueueSnackbar(t("saveSuccess", { name: res.name }), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(t("errors.definitions.saveError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
      history.push("/template-definitions");
    }
  };

  const getStorageType = (storageAccountId: string) =>
    toStorageType(
      storageAccountId.split(":")[1] as StorageProvider
    ) as AllowedStorage;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (hasErrors(errors)) {
      markAllTouched();
      return;
    }

    const templateDefinition = {
      name: formState.name,
      description: formState.description,
      storageType: getStorageType(formState.storageAccount),
      templateId: formState.metadataKey,
      mappings: formState.mappings
        .filter(({ key }) => !isBlank(key))
        .reduce((acc, { key, value }) => {
          const parsedValue = Array.isArray(value) ? value.join(";") : value;
          return { ...acc, [key]: parsedValue };
        }, {}),
    };

    if (state && isEditing) {
      const { definitionId } = state;
      saveDefinition(templateDefinition, definitionId);
    } else {
      saveDefinition(templateDefinition);
    }
  };

  function onStorageAccountReAuth() {
    loadStorageAccounts();
    setUnsyncedAccounts([]);
    setFailedAccountIds([]);
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <PageLoading loading={loading} />
        <Grid container spacing={5}>
          <Grid item lg={8}>
            <StyledPaper>
              <Box mb="1.8rem">
                <Typography gutterBottom variant="h6">
                  {isEditing ? t("from.edit.title") : t("form.title")}
                </Typography>
              </Box>
              <NameField
                value={formState.name}
                onChange={form.updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={(touched.name && errors.name) || ""}
              />
              <DescriptionField
                value={formState.description}
                onChange={form.updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.description && errors.description)}
                helperText={(touched.description && errors.description) || ""}
              />
              <OptionGroup>
                <StyledTitle>{t("form.storageAccount.title")}</StyledTitle>
                <StorageAccountSelector
                  label={t("form.storageAccount.label")}
                  isEditing={isEditing}
                  data-testid="td-storage-select"
                  excludedProviders={excludedProviders()}
                  disabledAccountIds={failedAccountIds}
                  value={toStorageAccountValue()}
                  onChange={handleSelectStorageAccount}
                  onBlur={handleBlur}
                  error={storageAccountError}
                  helperText={storageAccountErrorText}
                  onReAuthSuccess={onStorageAccountReAuth}
                />
              </OptionGroup>

              {isEditing && unsyncedAccounts.length > 0 && (
                <Box mb="16px">
                  <Alert
                    severity="warning"
                    data-testid="accounts-not-synced-alert"
                  >
                    {t("error.accountsNeedReAuth", {
                      accounts: unsyncedAccounts.map(a => a.user).join(", "),
                    })}{" "}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => history.push("/integrations?tab=ACCOUNTS")}
                    >
                      {t("error.listAccounts")}
                    </Link>
                  </Alert>
                </Box>
              )}

              {isEditing && templateNotFound && (
                <Box mb="16px">
                  <Alert severity="error">
                    {t("error.storageTemplateNotFound")}
                  </Alert>
                </Box>
              )}

              {templateNotFound || unsyncedAccounts.length > 0 ? (
                <OptionGroup style={{ paddingTop: 12, paddingBottom: 12 }}>
                  <TextField
                    label="Metadata ID"
                    variant="outlined"
                    value={formState.metadataKey}
                    disabled
                    fullWidth
                    error={Boolean(touched.metadataKey && errors.metadataKey)}
                    helperText={
                      (touched.metadataKey && errors.metadataKey) || ""
                    }
                  />
                </OptionGroup>
              ) : (
                <SelectMetadataKey
                  options={templates}
                  value={formState.metadataKey}
                  onChange={handleMetadataKeyChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.metadataKey && errors.metadataKey)}
                  helperText={(touched.metadataKey && errors.metadataKey) || ""}
                />
              )}
              {showMappingFields && template && (
                <Mappings
                  setValues={form.setValues}
                  formState={formState}
                  template={template}
                  errors={errors.mappings}
                  touched={touched.mappings}
                  onBlur={handleMappingBlur}
                  onAdd={addMapping}
                  onRemove={removeMapping}
                />
              )}
              {showMappingFields &&
                (templateNotFound || unsyncedAccounts.length > 0) && (
                  <ReadOnlyMappings mappings={formState.mappings} />
                )}
              <OptionGroup>
                <Button
                  type="submit"
                  size="large"
                  wording={t("form.submitBtn")}
                  data-testid="td-save-button"
                />
              </OptionGroup>
            </StyledPaper>
          </Grid>
        </Grid>
      </form>

      {loadError && <LoadErrorDialog error={loadError} />}
    </>
  );
}

const initValues = (): FormState => ({
  name: "",
  description: "",
  mappings: [{ id: random(), key: "", value: "" }],
  storageAccount: "",
  metadataKey: "",
});

function isBoxNotEnterprise(error: unknown) {
  return (
    isErrorWithCode(error) &&
    error.code.toLowerCase().includes("user does not have an enterprise")
  );
}

function isAccountNotSynced(error: unknown) {
  return isErrorWithCode(error) && error.code === "NOT_SYNCED";
}

function isAccountNotFound(error: unknown) {
  return isErrorWithCode(error) && error.code === "NOT_FOUND";
}

function errorSnackbarOptions(testId: string): OptionsObject {
  return {
    variant: "error",
    ContentProps: { "data-testid": testId } as Partial<SnackbarContentProps>,
  };
}

async function fetchTemplatesFromStorage(
  storageAccountId: string
): Promise<StorageTemplate[] | null> {
  const [account, storageType] = storageAccountId.split(":");
  const query = new URLSearchParams();
  query.append("storage", storageType);
  query.append("account", account);

  return await apiRequest<StorageTemplate[]>(
    "GET",
    `/storage-templates?${query.toString()}`
  );
}

async function fetchTemplatesFromSomeAccount(
  accounts: AccountWithState[],
  templateId: string
): Promise<
  [
    templates: StorageTemplate[],
    accountUse: AccountWithState | null,
    notSyncedAccounts: AccountWithState[]
  ]
> {
  let lastTemplates: StorageTemplate[] = [];
  let accountUse: AccountWithState | null = null;
  const notSyncedAccounts: AccountWithState[] = [];

  for (const account of accounts) {
    try {
      const templates = await fetchTemplatesFromStorage(account.id);
      if (templates && templates.some(t => t.id === templateId)) {
        return [templates, account, notSyncedAccounts];
      }
      lastTemplates = templates || [];
    } catch (error) {
      if (isAccountNotSynced(error)) {
        notSyncedAccounts.push(account);
      }
    }
  }

  return [lastTemplates, accountUse, notSyncedAccounts];
}

function convertMappingToKV(
  mapping: Record<string, string>
): { id: string; key: string; value: string | string[] }[] {
  return Object.keys(mapping).map(key => {
    // The value can be a variable, a static value or a list (separated by ";") like "test1;test2"
    const value = mapping[key].includes(";")
      ? mapping[key].split(";")
      : mapping[key];

    return { id: random(), key, value };
  });
}

function filterByType(
  accounts: AccountWithState[],
  storageType: StorageProvider
) {
  return accounts.filter(
    account => matchesStorageType(account.type, storageType) && account.enabled
  );
}
